import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Link, Navigate } from 'react-router-dom';
import * as userService from '../../services/userService';
import PaymentButton from '../../components/payments/PaymentButton';
import { convertRandsToUnits, formatUnits, hasValidRates, isFlatRate } from '../../config/siteConfig';
import { useSiteRates } from '../../context/SiteRatesContext';

function Billing() {
  const { user, account, hasRole } = useAuth();
  const { getSiteConfig } = useSiteRates();
  const siteConfig = getSiteConfig(user?.siteName);
  const isKwh = hasValidRates(siteConfig);
  const [loading, setLoading] = useState(false);
  const [amount, setAmount] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [pendingAmount, setPendingAmount] = useState(null);
  const [unitUuid, setUnitUuid] = useState(null);
  const [paymentInProgress, setPaymentInProgress] = useState(false);

  // Logging for debugging
  useEffect(() => {
    // console.log('User data:', user);
    // console.log('Account data:', account);
  }, [user, account]);

  // Fetch unit UUID from context data - no API call needed
  useEffect(() => {
    // Use existing data from context without making API calls
    if (user?.unitUuid) {
      // console.log(`Using unitUuid from user context: ${user.unitUuid}`);
      setUnitUuid(user.unitUuid);
    } else if (account?.unitUuid) {
      // console.log(`Using unitUuid from account context: ${account.unitUuid}`);
      setUnitUuid(account.unitUuid);
    } else if (user?.siteUuid && user?.unitId) {
      // We have the siteUuid and unitId, we can directly fetch the unit info without going through site lookup
      // console.log(`We have siteUuid: ${user.siteUuid} and unitId: ${user.unitId}, fetching unit info directly`);
      
      // Only make this call if absolutely necessary
      const getUnitData = async () => {
        try {
          // Pass the siteUuid directly to avoid site name lookup
          const unitData = await userService.getUnitByNameAndId(
            user.siteName || '',  // Still pass siteName for logging
            user.unitId,
            user.siteUuid // Pass siteUuid to avoid lookup
          );
          
          if (unitData && unitData.uuid) {
            setUnitUuid(unitData.uuid);
          }
        } catch (error) {
          // console.error('Error fetching unit data:', error);
        }
      };
      
      getUnitData();
    } else if (user?.uuid && user?.siteUuid) {
      // If we have partial information, store it for the PaymentButton
      // console.log(`No unitUuid available, but we have user UUID: ${user.uuid} and site UUID: ${user.siteUuid}`);
      // The PaymentButton will handle this case
    } else {
      // console.warn('No unit UUID available in context data');
    }
  }, [user, account]);

  // Check if user has any staff role (ADMIN, MANAGEMENT, OPERATOR)
  const isStaff = user && (hasRole('ADMIN') || hasRole('MANAGEMENT') || hasRole('OPERATOR'));

  // Redirect staff users to admin dashboard
  if (user && isStaff) {
    return <Navigate to="/admin" replace />;
  }

  // If no user is logged in, redirect to login
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Prevent multiple submissions
    if (paymentInProgress) {
      // console.log('Payment already in progress, ignoring additional click');
      return;
    }
    
    // Validate amount
    if (!amount || isNaN(parseFloat(amount)) || parseFloat(amount) <= 0) {
      setErrorMessage('Please enter a valid amount greater than 0');
      return;
    }
    
    const purchaseAmount = parseFloat(amount);
    
    // Clear any previous error messages
    setErrorMessage('');
    
    // Check if the amount exceeds the limit requiring confirmation
    if (purchaseAmount > 2000) {
      setShowConfirmation(true);
      setPendingAmount(purchaseAmount);
      return;
    }
    
    // Small delay to ensure we set the state before the PaymentButton processes
    setPaymentInProgress(true);
  };
  
  // Handle confirmation dialog response
  const handleConfirmPurchase = () => {
    if (pendingAmount && !paymentInProgress) {
      setShowConfirmation(false);
      // Mark payment as in progress to prevent multiple submissions
      setPaymentInProgress(true);
    }
  };
  
  // Handle cancellation from the confirmation dialog
  const handleCancelPurchase = () => {
    setShowConfirmation(false);
    setPendingAmount(null);
  };

  // Handle Yoco checkout created event
  const handleCheckoutCreated = (checkout) => {
    // console.log('Checkout created:', checkout);
    // Set payment in progress to prevent multiple clicks
    setPaymentInProgress(true);
    // The PaymentButton will handle redirection to Yoco
  };

  // Handle errors from Yoco checkout creation
  const handleCheckoutError = (error) => {
    // console.error('Checkout error:', error);
    setErrorMessage(error?.message || 'Failed to create payment checkout. Please try again.');
    setLoading(false);
    setPaymentInProgress(false);
  };

  // Predefined amounts for quick selection
  const predefinedAmounts = [50, 100, 200, 500];

  return (
    <div className="max-w-4xl mx-auto mt-8 mb-12">
      <div className="mb-8">
        <h1 className="text-hooray-red text-3xl font-bold mb-2 font-mont">Billing</h1>
        <p className="text-hooray-red">Purchase electricity for your unit</p>
      </div>

      {/* Confirmation Modal */}
      {showConfirmation && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
          <div className="relative mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3 text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-yellow-100">
                <svg className="h-6 w-6 text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <h3 className="text-lg leading-6 font-medium text-gray-900 mt-2">Large Purchase Confirmation</h3>
              <div className="mt-2 px-7 py-3">
                <p className="text-sm text-gray-500">
                  You are about to purchase <span className="font-bold">R{pendingAmount?.toFixed(2)}</span> worth of electricity. 
                  Are you sure you want to proceed with this large purchase?
                </p>
              </div>
              <div className="flex justify-between mt-3 px-4 py-3">
                <button
                  onClick={handleCancelPurchase}
                  className="px-4 py-2 bg-gray-300 text-gray-800 text-base font-medium rounded-md shadow-sm hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-300"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmPurchase}
                  className="px-4 py-2 bg-yellow-500 text-white text-base font-medium rounded-md shadow-sm hover:bg-yellow-600 focus:outline-none focus:ring-2 focus:ring-yellow-300"
                >
                  Confirm Purchase
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        {/* Current Balance Card */}
        <div className="col-span-1 bg-white shadow-md rounded-lg p-6">
          <h2 className="hooray-dark-text text-sm font-semibold uppercase mb-2">Current Balance</h2>
          {isKwh ? (
            <>
              <div className="flex items-baseline mb-2">
                <span className="text-3xl font-bold text-primary">
                  {formatUnits(convertRandsToUnits(account?.balance || 0, siteConfig.currentRates.peak))}
                </span>
                <span className="text-lg font-semibold text-primary ml-1">Units (kWh)</span>
              </div>
              <div className="text-sm text-gray-600 mb-4">
                R {account?.balance ? account.balance.toFixed(2) : '0.00'}
              </div>
              <div className="pt-3 border-t border-gray-200 mb-4">
                <h3 className="text-xs font-semibold uppercase text-gray-500 mb-2">
                  {isFlatRate(siteConfig.currentRates) ? 'Rate' : 'Rates'}
                </h3>
                <div className="space-y-1 text-sm">
                  {isFlatRate(siteConfig.currentRates) ? (
                    <div className="flex justify-between">
                      <span className="text-gray-600">All day (flat rate):</span>
                      <span className="font-medium">R{siteConfig.currentRates.peak.toFixed(2)}/kWh</span>
                    </div>
                  ) : (
                    <>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Peak (16:00-08:00):</span>
                        <span className="font-medium">R{siteConfig.currentRates.peak.toFixed(2)}/kWh</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Off-Peak (08:00-16:00):</span>
                        <span className="font-medium">R{siteConfig.currentRates.offPeak.toFixed(2)}/kWh</span>
                      </div>
                    </>
                  )}
                </div>
              </div>
              <p className="text-sm text-gray-600">
                Your current balance allows you to continue using electricity. 
                When your balance gets low, consider purchasing more to avoid interruptions.
              </p>
            </>
          ) : (
            <>
              <div className="flex items-baseline mb-4">
                <span className="text-3xl font-bold text-primary">
                  R {account?.balance ? account.balance.toFixed(2) : '0.00'}
                </span>
              </div>
              <p className="text-sm text-gray-600">
                Your current balance allows you to continue using electricity. 
                When your balance gets low, consider purchasing more to avoid interruptions.
              </p>
            </>
          )}
        </div>

        {/* Payment Form */}
        <div className="col-span-2 bg-white shadow-md rounded-lg p-6">
          <h2 className="text-xl font-semibold mb-4">Purchase Electricity</h2>
          
          {successMessage && (
            <div className="bg-green-100 border-l-4 border-green-500 text-green-700 p-4 mb-4" role="alert">
              <p>{successMessage}</p>
            </div>
          )}
          
          {errorMessage && (
            <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-4" role="alert">
              <p>{errorMessage}</p>
            </div>
          )}
          
          {paymentInProgress && (
            <div className="bg-blue-100 border-l-4 border-blue-500 text-blue-700 p-4 mb-4" role="alert">
              <p className="flex items-center">
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Payment processing - please wait while we redirect you to the payment page...
              </p>
            </div>
          )}
          
          <form onSubmit={handleSubmit}>
            {/* Quick amount selection */}
            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2">
                Quick Select Amount
              </label>
              <div className="flex flex-wrap gap-2">
                {predefinedAmounts.map(amt => (
                  <button
                    key={amt}
                    type="button"
                    className={`px-4 py-2 rounded-md ${
                      parseFloat(amount) === amt 
                        ? 'bg-hooray-yellow text-hooray-red' 
                        : 'bg-hooray-red text-white hover:bg-hooray-yellow hover:text-hooray-red'
                    }`}
                    onClick={() => setAmount(amt.toString())}
                  >
                    R{amt}
                  </button>
                ))}
              </div>
            </div>

            {/* Custom amount input */}
            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="amount">
                Amount (R)
              </label>
              <input
                type="number"
                id="amount"
                min="1"
                step="0.01"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                placeholder="Enter amount"
                required
              />
              {parseFloat(amount) > 2000 && (
                <p className="text-yellow-600 text-xs mt-1">
                  Purchases over R2000 will require confirmation.
                </p>
              )}
            </div>

            {/* Submit button */}
            <div className="flex items-center justify-between">
              <PaymentButton
                amount={amount ? Math.round(parseFloat(amount) * 100) : 0} // Convert to cents
                accountUuid={account?.uuid}
                unitUuid={unitUuid || user?.unitUuid || account?.unitUuid}
                onCheckoutCreated={handleCheckoutCreated}
                onError={handleCheckoutError}
                disabled={paymentInProgress}
              />
            </div>
          </form>
        </div>
      </div>

      {/* Transaction Information */}
      <div className="bg-white shadow-md rounded-lg p-6 mb-8">
        <h2 className="text-xl font-semibold mb-4 font-mont">About Electricity Purchases</h2>
        <div className="space-y-4">
          <p>
            When you purchase electricity, your account balance is updated immediately. 
            The purchased amount will be available for use right away.
          </p>
          <p>
            Your purchase history is available in your transaction history. 
            You can view detailed information about all your purchases there. <b>(Coming soon)</b>
          </p>
          <div className="flex justify-end">
            <Link to="/electricity-data" className="text-hooray-red hover:text-hooray-yellow">
              View Electricity Data →
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Billing; 